import { refreshAccessToken } from 'api/utils/refreshTokenUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DASHBOARD, LOGIN, STORE } from 'constants/routes';
import useClearSession from 'hooks/useClearSession';
import i18n from 'i18n';
import { useNavigate } from 'react-router';
import { useAuthStore } from 'stores/authStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { toastr } from 'utils';
import { removeBrand } from 'utils/brandService';
import { selectProfile } from 'utils/selectProfile';
import AUTH_KEYS from '../auth.keys';
import AUTH_MUTATIONS from '../auth.mutation';
import AUTH_QUERIES from '../auth.queries';

/**
 * Hook for user login
 * @returns {Object} Mutation object with login function
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  return useMutation({
    mutationFn: AUTH_MUTATIONS.LOGIN,
    onSuccess: async (data, variables) => {
      const { access } = data;
      const { redirectAddress } = variables;

      setAccessToken(access);
      const ramblaUser = selectProfile(await AUTH_QUERIES.GET_USER_DETAILS());
      queryClient.setQueryData(AUTH_KEYS.USER_PROFILE(), ramblaUser);
      if (redirectAddress) {
        const isAdminUser = Boolean(ramblaUser?.admin_details);
        const nextRoute = isAdminUser ? DASHBOARD : redirectAddress;

        navigate(nextRoute);
      }
    },
    onError: () => {
      clearAuth();
    },
  });
};

/**
 * Hook for user logout
 * @returns {Object} Mutation object — always clears local session (best-effort revoke)
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const clearSession = useClearSession();

  return useMutation({
    mutationFn: AUTH_MUTATIONS.LOGOUT,
    onSettled: () => {
      clearSession();
      navigate(LOGIN);
      toastr.success(i18n.t('toastr:logoutSuccessful'));
    },
  });
};

export const useAutoLogin = (options = {}) =>
  useQuery({
    queryKey: AUTH_KEYS.USER_PROFILE(),
    queryFn: async () => {
      if (!useAuthStore.getState().accessToken) {
        try {
          await refreshAccessToken();
        } catch {
          return null;
        }
      }

      return selectProfile(await AUTH_QUERIES.GET_USER_DETAILS());
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });

/**
 * Hook for user registration
 * @returns {Object} Mutation object with register function
 */
export const useRegister = () => {
  const loginMutation = useLogin();
  const clearAuth = useAuthStore((state) => state.clearAuth);

  return useMutation({
    mutationFn: AUTH_MUTATIONS.REGISTER,
    onSuccess: async (data, variables) => {
      const { email, password } = variables;
      await loginMutation.mutateAsync({ email, password });
    },
    onError: () => {
      clearAuth();
    },
  });
};

export const useDeleteMe = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const clearSelectedProfile = useSelectedProfileStore((state) => state.clearSelectedProfile);

  return useMutation({
    mutationFn: AUTH_MUTATIONS.DELETE_ME,
    onSuccess: () => {
      clearAuth();
      clearSelectedProfile();
      removeBrand();
      queryClient.clear();
      navigate(LOGIN);
    },
  });
};

export const useSendResetPasswordCode = () =>
  useMutation({
    mutationFn: AUTH_MUTATIONS.SEND_RESET_PASSWORD_CODE,
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: AUTH_MUTATIONS.RESET_PASSWORD,
  });

/**
 * Hook for fetching invitation details by uid
 * @param {string} uid - Invitation uid
 * @returns {Object} Query object with invitation data
 */
export const useGetInvitation = (uid) =>
  useQuery({
    queryKey: AUTH_KEYS.INVITATION(uid),
    queryFn: () => AUTH_QUERIES.GET_INVITATION({ uid }),
    enabled: !!uid,
    retry: false,
  });

export const useDeferredLogin = () =>
  useQuery({
    queryKey: AUTH_KEYS.DEFERRED_LOGIN(),
    queryFn: AUTH_QUERIES.GET_DEFERRED_LOGIN,
    enabled: false,
    retry: false,
    refetchOnWindowFocus: false,
  });

/**
 * Hook for account activation
 * @returns {Object} Mutation object with activate account function
 */
export const useActivateAccount = () => {
  const loginMutation = useLogin();
  const navigation = useNavigate();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  return useMutation({
    mutationFn: AUTH_MUTATIONS.ACTIVATE_ACCOUNT,
    onSuccess: async (data, variables) => {
      const { email, password } = variables;
      if (password) {
        await loginMutation.mutateAsync({ email, password, redirectAddress: STORE });
      } else {
        navigation(LOGIN);
      }
    },
    onError: () => {
      clearAuth();
    },
  });
};
